import type { StoreProduct, StoreProductUpdate } from '@erp/shared';
import { ArrowLeft, Columns3, Sparkles, Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Badge, Card, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Skeleton,
} from '../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState, FormField, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { aiGenerate } from '../../lib/aiContent.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { cn } from '../../lib/cn.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';

import { TranslationEditor } from './TranslationEditor.js';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface Draft { isPublished: boolean; featured: boolean }
type Mode = { kind: 'list' } | { kind: 'edit'; product: StoreProduct };

type PublishFilter = 'all' | 'published' | 'unpublished';
type StatusFilter = 'all' | 'active' | 'inactive';
type VisibilityFilter = 'all' | 'visible' | 'hidden';

// --- persisted UI state (per tenant, localStorage), same idiom as the items list.
const colsKey = (t: string | null): string => `erp-cms-products-cols:${t ?? 'x'}`;
const filtersKey = (t: string | null): string => `erp-cms-products-filters:${t ?? 'x'}`;
function loadJson<T extends object>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<T>) } : fallback;
  } catch {
    return fallback;
  }
}
function saveJson(key: string, value: unknown): void {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}
interface SavedFilters {
  publishFilter?: PublishFilter;
  statusFilter?: StatusFilter;
  visibilityFilter?: VisibilityFilter;
  featuredOnly?: boolean;
  categoryFilter?: string;
  brandFilter?: string;
}

// Hidden until opted in via the Columns menu — the table stays readable while
// still carrying everything a store owner might want to sort or scan by.
const DEFAULT_HIDDEN_COLS = new Set(['unit', 'stock', 'views', 'added', 'flags']);
const LOCKED_COLS = new Set(['name', 'actions']);

export function CmsProductsPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const [products, setProducts] = useState<StoreProduct[] | null>(null);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });

  const saved = useMemo(() => loadJson<SavedFilters>(filtersKey(tenantId), {}), [tenantId]);
  const [publishFilter, setPublishFilter] = useState<PublishFilter>(saved.publishFilter ?? 'all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(saved.statusFilter ?? 'all');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>(saved.visibilityFilter ?? 'all');
  const [featuredOnly, setFeaturedOnly] = useState(saved.featuredOnly ?? false);
  const [categoryFilter, setCategoryFilter] = useState(saved.categoryFilter ?? 'all');
  const [brandFilter, setBrandFilter] = useState(saved.brandFilter ?? 'all');
  const [colVis, setColVis] = useState<Record<string, boolean>>(() => loadJson<Record<string, boolean>>(colsKey(tenantId), {}));

  useEffect(() => {
    saveJson(filtersKey(tenantId), { publishFilter, statusFilter, visibilityFilter, featuredOnly, categoryFilter, brandFilter });
  }, [tenantId, publishFilter, statusFilter, visibilityFilter, featuredOnly, categoryFilter, brandFilter]);
  useEffect(() => { saveJson(colsKey(tenantId), colVis); }, [tenantId, colVis]);

  const hydrate = (items: StoreProduct[]): void => {
    setProducts(items);
    const d: Record<string, Draft> = {};
    for (const p of items) d[p.itemId] = { isPublished: p.isPublished, featured: p.featured };
    setDraft(d);
  };

  const load = (): void => {
    void api().get<{ items: StoreProduct[] }>('/cms/products').then((r) => hydrate(r.data.items)).catch(() => setProducts([]));
  };
  useEffect(load, []);

  const setD = (id: string, patch: Partial<Draft>): void => setDraft((m) => ({ ...m, [id]: { ...m[id]!, ...patch } }));

  const save = async (p: StoreProduct): Promise<void> => {
    const d = draft[p.itemId]!;
    setSavingId(p.itemId);
    try {
      // Store price is read-only here — it's owned by the item / the detail form,
      // so the row save must not send it or it would clobber an override.
      const r = await api().put<StoreProduct>(`/cms/products/${p.itemId}`, {
        isPublished: d.isPublished,
        featured: d.featured,
      });
      setProducts((list) => (list ?? []).map((x) => (x.itemId === p.itemId ? r.data : x)));
      toast.success(t('products.saved'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSavingId(null);
    }
  };

  const money = (v: string | null, ccy: string | null): string =>
    v && ccy ? formatMoney(v, { locale: i18n.language, currency: ccy }) : '—';

  const columns: DataTableColumn<StoreProduct>[] = [
    {
      id: 'name', header: t('products.name'), sortValue: (r) => r.name,
      searchValue: (r) => `${r.name} ${r.code} ${r.barcode ?? ''} ${r.categoryName ?? ''} ${r.brandName ?? ''}`,
      cell: (r) => (
        <span>
          <button type="button" className="text-start font-medium text-brand hover:underline" onClick={() => setMode({ kind: 'edit', product: r })}>{r.name}</button>
          <span className="ms-2 font-mono text-xs text-fg-muted">{r.code}</span>
          {r.isPublished && <Badge variant="success" className="ms-2">{t('products.live')}</Badge>}
        </span>
      ),
    },
    {
      id: 'barcode', header: t('products.barcode'),
      cellClassName: 'font-mono text-xs', sortValue: (r) => r.barcode ?? '',
      cell: (r) => r.barcode ?? <span className="text-fg-subtle">—</span>,
    },
    {
      id: 'category', header: t('products.category'), sortValue: (r) => r.categoryName ?? '',
      cell: (r) => r.categoryName ?? <span className="text-fg-subtle">—</span>,
    },
    {
      id: 'brand', header: t('products.brand'), sortValue: (r) => r.brandName ?? '',
      cell: (r) => r.brandName ?? <span className="text-fg-subtle">—</span>,
    },
    {
      id: 'unit', header: t('products.unit'), sortValue: (r) => r.unitOfMeasure ?? '',
      cell: (r) => r.unitOfMeasure ?? <span className="text-fg-subtle">—</span>,
    },
    {
      id: 'base', header: t('products.basePrice'), align: 'end', cellClassName: 'font-mono tabular-nums',
      sortValue: (r) => Number(r.salePrice ?? 0), cell: (r) => money(r.salePrice, r.saleCurrencyCode),
    },
    {
      // Read-only: shows what the store actually charges — the override when one
      // exists, otherwise the item's base price. Set it on the product's page.
      id: 'storePrice', header: t('products.storePrice'), align: 'end', cellClassName: 'font-mono tabular-nums',
      sortValue: (r) => Number(r.storePrice ?? r.salePrice ?? 0),
      cell: (r) => (
        <span className={r.storePrice ? 'font-semibold text-fg' : 'text-fg-muted'} title={r.storePrice ? t('products.storePriceOverride') : t('products.storePriceInherited')}>
          {money(r.storePrice ?? r.salePrice, r.saleCurrencyCode)}
          {r.storePrice && <span className="ms-1 text-[10px] font-normal uppercase text-brand">{t('products.overrideTag')}</span>}
        </span>
      ),
    },
    {
      id: 'stock', header: t('products.stock'), align: 'end', cellClassName: 'font-mono tabular-nums',
      sortValue: (r) => r.stock ?? -1,
      cell: (r) => r.stock == null
        ? <span className="text-fg-subtle">—</span>
        : <span className={r.stock <= 0 ? 'text-danger' : undefined}>{r.stock}</span>,
    },
    {
      id: 'views', header: t('products.views'), align: 'end', cellClassName: 'tabular-nums',
      sortValue: (r) => r.views, cell: (r) => r.views || <span className="text-fg-subtle">0</span>,
    },
    {
      id: 'added', header: t('products.added'), sortValue: (r) => r.createdAt,
      cell: (r) => <span className="text-xs text-fg-muted">{new Date(r.createdAt).toLocaleDateString(i18n.language)}</span>,
    },
    {
      // Why a published product can still be invisible in the store.
      id: 'flags', header: t('products.flags'),
      sortValue: (r) => `${r.isActive ? 1 : 0}${r.isHidden ? 0 : 1}`,
      cell: (r) => (
        <span className="flex flex-wrap gap-1">
          {!r.isActive && <Badge variant="secondary">{t('products.inactive')}</Badge>}
          {r.isHidden && <Badge variant="warning">{t('products.hiddenInStore')}</Badge>}
          {r.isActive && !r.isHidden && <span className="text-xs text-fg-subtle">—</span>}
        </span>
      ),
    },
    {
      id: 'publish', header: t('products.publish'),
      cell: (r) => <input type="checkbox" className="h-4 w-4" checked={draft[r.itemId]?.isPublished ?? false} disabled={!canWrite} onChange={(e) => setD(r.itemId, { isPublished: e.target.checked })} aria-label={t('products.publish')} />,
    },
    {
      id: 'featured', header: t('products.featured'),
      cell: (r) => <input type="checkbox" className="h-4 w-4" checked={draft[r.itemId]?.featured ?? false} disabled={!canWrite} onChange={(e) => setD(r.itemId, { featured: e.target.checked })} aria-label={t('products.featured')} />,
    },
    {
      id: 'actions', header: '', align: 'end',
      cell: (r) => canWrite ? <Button variant="secondary" size="sm" loading={savingId === r.itemId} onClick={() => void save(r)}>{tc('actions.save')}</Button> : null,
    },
  ];

  const isColVisible = (id: string): boolean => colVis[id] ?? !DEFAULT_HIDDEN_COLS.has(id);
  const visibleColumns = columns.filter((c) => LOCKED_COLS.has(c.id) || isColVisible(c.id));
  const hideableColumns = columns.filter((c) => !LOCKED_COLS.has(c.id));

  // Filter options come from the loaded rows — no extra requests, and they only
  // ever list categories/brands that actually have sellable products.
  const categoryOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of products ?? []) if (p.categoryId && p.categoryName) m.set(p.categoryId, p.categoryName);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);
  const brandOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of products ?? []) if (p.brandId && p.brandName) m.set(p.brandId, p.brandName);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);

  const anyFilter = publishFilter !== 'all' || statusFilter !== 'all' || visibilityFilter !== 'all'
    || featuredOnly || categoryFilter !== 'all' || brandFilter !== 'all';
  const clearFilters = (): void => {
    setPublishFilter('all'); setStatusFilter('all'); setVisibilityFilter('all');
    setFeaturedOnly(false); setCategoryFilter('all'); setBrandFilter('all');
  };

  const visible = (products ?? []).filter((p) =>
    (publishFilter === 'all' || (publishFilter === 'published' ? p.isPublished : !p.isPublished))
    && (statusFilter === 'all' || (statusFilter === 'active' ? p.isActive : !p.isActive))
    && (visibilityFilter === 'all' || (visibilityFilter === 'hidden' ? p.isHidden : !p.isHidden))
    && (!featuredOnly || p.featured)
    && (categoryFilter === 'all' || p.categoryId === categoryFilter)
    && (brandFilter === 'all' || p.brandId === brandFilter),
  );

  if (mode.kind === 'edit') {
    return <ProductForm product={mode.product} onDone={() => { setMode({ kind: 'list' }); load(); }} onCancel={() => setMode({ kind: 'list' })} />;
  }

  const selectCls = 'h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted';
  const pill = (on: boolean): string => cn(
    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition',
    on ? 'border-transparent bg-brand text-brand-contrast' : 'border-border bg-bg text-fg-muted hover:text-fg',
  );

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('products.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('products.subtitle')}</p>
      </header>
      {!products ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : products.length === 0 ? (
        <EmptyState title={t('products.empty')} description={t('products.emptyHint')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={visibleColumns}
            data={visible}
            rowKey={(r) => r.itemId}
            searchPlaceholder={t('products.search')}
            rowClassName={(r) => (r.isActive ? undefined : 'opacity-60')}
            toolbar={
              <div className="flex flex-wrap items-center gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="h-8 gap-1.5 rounded-full">
                      <Columns3 className="h-4 w-4" aria-hidden />
                      {t('products.columnsMenu')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-80 overflow-auto">
                    <DropdownMenuLabel>{t('products.columnsMenu')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {hideableColumns.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        checked={isColVisible(col.id)}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(v) => setColVis((m) => ({ ...m, [col.id]: v }))}
                      >
                        {typeof col.header === 'string' && col.header ? col.header : col.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Published — the one that answers "why isn't this in my store?" */}
                <div className="inline-flex rounded-full border border-border p-0.5">
                  {(['all', 'published', 'unpublished'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={publishFilter === f}
                      onClick={() => setPublishFilter(f)}
                      className={cn(
                        'inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition',
                        publishFilter === f ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg',
                      )}
                    >
                      {f === 'all'
                        ? t('products.filterAll')
                        : f === 'published'
                          ? t('products.filterPublished')
                          : t('products.filterUnpublished')}
                    </button>
                  ))}
                </div>

                {categoryOptions.length > 0 && (
                  <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label={t('products.allCategories')} className={selectCls}>
                    <option value="all">{t('products.allCategories')}</option>
                    {categoryOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                )}
                {brandOptions.length > 0 && (
                  <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} aria-label={t('products.allBrands')} className={selectCls}>
                    <option value="all">{t('products.allBrands')}</option>
                    {brandOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                )}
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} aria-label={t('products.allStatuses')} className={selectCls}>
                  <option value="all">{t('products.allStatuses')}</option>
                  <option value="active">{t('products.active')}</option>
                  <option value="inactive">{t('products.inactive')}</option>
                </select>
                <select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value as VisibilityFilter)} aria-label={t('products.allVisibility')} className={selectCls}>
                  <option value="all">{t('products.allVisibility')}</option>
                  <option value="visible">{t('products.visibleInStore')}</option>
                  <option value="hidden">{t('products.hiddenInStore')}</option>
                </select>
                <button type="button" aria-pressed={featuredOnly} onClick={() => setFeaturedOnly((v) => !v)} className={pill(featuredOnly)}>
                  <Star className="h-3.5 w-3.5" aria-hidden />
                  {t('products.featured')}
                </button>
                {anyFilter && (
                  <button type="button" onClick={clearFilters} className="text-xs font-medium text-fg-muted transition hover:text-danger">
                    {t('products.clearFilters')}
                  </button>
                )}
                <span className="text-xs text-fg-muted">{t('products.shownOfTotal', { shown: visible.length, total: products.length })}</span>
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
}

function ProductForm({ product, onDone, onCancel }: { product: StoreProduct; onDone: () => void; onCancel: () => void }): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const aiEnabled = moduleEnabled(useEnabledModules(tenantId), 'ai');
  const [slug, setSlug] = useState(product.slug ?? '');
  const [seoTitle, setSeoTitle] = useState(product.seoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(product.seoDescription ?? '');
  const [storePrice, setStorePrice] = useState(product.storePrice ?? '');
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const generateSeo = async (): Promise<void> => {
    setAiBusy(true);
    try {
      const r = await aiGenerate({ task: 'seo', name: product.name, source: product.seoDescription ?? undefined });
      if (r.seo) { setSeoTitle(r.seo.title); setSeoDescription(r.seo.description); }
      else toast.error(t('products.aiFailed'));
    } catch {
      toast.error(t('products.aiFailed'));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (slug.trim() && !SLUG.test(slug.trim())) { toast.error(t('products.badSlug')); return; }
    const price = storePrice.trim();
    if (price && !/^\d+(\.\d{1,4})?$/.test(price)) { toast.error(t('products.badPrice')); return; }
    setSaving(true);
    const payload: StoreProductUpdate = {
      slug: slug.trim() || null,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
      storePrice: price || null,
    };
    try {
      await api().put(`/cms/products/${product.itemId}`, payload);
      toast.success(t('products.saved'));
      onDone();
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? t('products.slugTaken') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const basePrice = product.salePrice && product.saleCurrencyCode
    ? formatMoney(product.salePrice, { locale: i18n.language, currency: product.saleCurrencyCode })
    : '—';

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <button type="button" onClick={onCancel} className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />{t('products.back')}
      </button>
      <h1 className="text-2xl font-semibold">{product.name} <span className="font-mono text-sm text-fg-muted">{product.code}</span></h1>

      <Card className="flex flex-col gap-4 p-5">
        <FormField id="prod-slug" label={t('products.slug')} helperText={t('products.slugHint')}>
          <Input id="prod-slug" value={slug} disabled={!canWrite} onChange={(e) => setSlug(e.target.value.toLowerCase())} maxLength={160} placeholder="blue-widget" />
        </FormField>
        {/* The one place the store override is editable — the list shows it read-only. */}
        <FormField id="prod-store-price" label={t('products.storePrice')} helperText={t('products.storePriceHint', { base: basePrice })}>
          <Input id="prod-store-price" value={storePrice} disabled={!canWrite} onChange={(e) => setStorePrice(e.target.value)} inputMode="decimal" placeholder={product.salePrice ?? ''} className="max-w-[12rem]" />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="prod-seo-title" label={t('pages.seoTitle')}>
            <Input id="prod-seo-title" value={seoTitle} disabled={!canWrite} onChange={(e) => setSeoTitle(e.target.value)} maxLength={200} />
          </FormField>
          <FormField id="prod-seo-desc" label={t('pages.seoDescription')}>
            <Input id="prod-seo-desc" value={seoDescription} disabled={!canWrite} onChange={(e) => setSeoDescription(e.target.value)} maxLength={320} />
          </FormField>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && <Button className="w-fit" onClick={() => void save()} loading={saving}>{tc('actions.save')}</Button>}
          {canWrite && aiEnabled && (
            <Button type="button" variant="secondary" className="w-fit" loading={aiBusy} disabled={saving} onClick={() => void generateSeo()}>
              <Sparkles className="h-4 w-4" aria-hidden />
              {t('products.generateSeo')}
            </Button>
          )}
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('pages.translate')}</h2>
        <TranslationEditor
          entityType="item"
          entityId={product.itemId}
          disabled={!canWrite}
          fields={[
            { key: 'name', label: t('products.name') },
            { key: 'description', label: t('products.description'), type: 'richtext' },
          ]}
          aiSources={aiEnabled ? { name: product.name } : undefined}
        />
      </Card>
    </div>
  );
}
